import { PermissionKey, RoleDefinition, RoleShell, ViewScope } from 'src/types/permissions.types';

const ADMIN_PERMISSIONS: PermissionKey[] = [
  'cohorts.view_all',
  'cohorts.view_assigned',
  'cohorts.create',
  'cohorts.edit',
  'cohorts.delete',
  'cohorts.manage_teachers',
  'cohorts.manage_students',
  'classes.view_all',
  'classes.create',
  'classes.edit',
  'classes.delete',
  'students.view_all',
  'students.view_assigned',
  'students.create',
  'students.edit',
  'students.delete',
  'students.manage',
  'teachers.view_all',
  'teachers.manage',
  'roles.manage',
  'transactions.create',
  'transactions.view_all',
  'transaction_types.manage',
  'loans.approve',
  'payroll.access',
  'whammies.view_all',
  'whammies.view_assigned',
  'whammies.create',
  'whammies.edit',
  'whammies.delete',
  'whammies.assign',
  'street_cred.view_all',
  'street_cred.view_assigned',
  'street_cred.manage',
];

const TEACHER_PERMISSIONS: PermissionKey[] = [
  'cohorts.view_assigned',
  'cohorts.manage_students',
  'students.view_assigned',
  'students.create',
  'students.edit',
  'students.manage',
  'loans.approve',
  'transactions.create',
  'transactions.view_assigned',
  'whammies.view_assigned',
  'whammies.create',
  'whammies.assign',
  'street_cred.view_assigned',
  'street_cred.manage',
];

/**
 * Roles that ship with the app. The `roles` table overrides these at runtime;
 * they stay here so the app still works before that table loads.
 * Keep in sync with supabase/migrations/20260921_dynamic_roles.sql.
 */
export const BUILT_IN_ROLES: RoleDefinition[] = [
  {
    name: 'SYSTEM_CREATOR',
    label: 'System Creator',
    base_route: '/admin',
    permissions: ['*'],
    is_system: true,
  },
  { name: 'ADMIN', label: 'School Admin', base_route: '/admin', permissions: ADMIN_PERMISSIONS, is_system: true },
  { name: 'TEACHER', label: 'Educator', base_route: '/teacher', permissions: TEACHER_PERMISSIONS, is_system: true },
  // A Banker is a trusted student with teacher powers over the cohorts they are enrolled in.
  { name: 'BANKER', label: 'Student Banker', base_route: '/teacher', permissions: TEACHER_PERMISSIONS, is_system: true },
  {
    name: 'STUDENT',
    label: 'Scholar',
    base_route: '/student',
    permissions: ['students.view_self', 'transactions.view_self', 'street_cred.view_self', 'whammies.view_assigned'],
    is_system: true,
  },
];

/** Live registry, keyed by upper-case role name. Filled by `setRoleDefinitions`. */
export const ROLE_PERMISSIONS: Record<string, PermissionKey[]> = {};
export const ROLE_BASE_ROUTES: Record<string, RoleShell> = {};
export const ROLE_LABELS: Record<string, string> = {};

function normalizeRole(role: string): string {
  return role.toUpperCase().trim();
}

/**
 * Replaces the live role registry. Built-in roles are always present, so a
 * missing or partial `roles` table never locks anyone out.
 */
export function setRoleDefinitions(roles: RoleDefinition[]): void {
  [ROLE_PERMISSIONS, ROLE_BASE_ROUTES, ROLE_LABELS].forEach((map) => {
    Object.keys(map).forEach((key) => delete map[key]);
  });
  [...BUILT_IN_ROLES, ...roles].forEach((role) => {
    const name = normalizeRole(role.name);
    ROLE_PERMISSIONS[name] = role.permissions || [];
    ROLE_BASE_ROUTES[name] = role.base_route;
    ROLE_LABELS[name] = role.label || name;
  });
}

setRoleDefinitions([]);

/** Display name for a role, for example 'BANKER' => 'Student Banker'. */
export function getRoleLabel(role?: string): string {
  if (!role) return '';
  return ROLE_LABELS[normalizeRole(role)] || role;
}

/**
 * Returns the list of permission keys assigned to a given role.
 */
export function getRolePermissions(role?: string): PermissionKey[] {
  if (!role) return [];
  const normalized = role.toUpperCase().trim();
  return ROLE_PERMISSIONS[normalized] || [];
}

/**
 * Checks whether a given role possesses a specific permission key.
 */
export function hasRolePermission(role: string | undefined, permission: PermissionKey | string): boolean {
  if (!role) return false;
  const permissions = getRolePermissions(role);
  if (permissions.includes('*')) return true;
  return permissions.includes(permission as PermissionKey);
}

/**
 * Resolves the appropriate base route for a role with optional subpath.
 * Example: getRoleBaseRoute('TEACHER', '/cohorts') => '/teacher/cohorts'
 * Example: getRoleBaseRoute('ADMIN', '/cohorts') => '/admin/cohorts'
 */
export function getRoleBaseRoute(role?: string, subpath?: string): string {
  const normalized = (role || 'ADMIN').toUpperCase().trim();
  const base = ROLE_BASE_ROUTES[normalized] || '/admin';
  if (!subpath) return base;
  const cleanSubpath = subpath.startsWith('/') ? subpath : `/${subpath}`;
  return `${base}${cleanSubpath}`;
}

/**
 * Evaluates semantic action and resource against role permissions.
 * Example: canPerformAction('TEACHER', 'create', 'cohorts') => false
 * Example: canPerformAction('ADMIN', 'create', 'cohorts') => true
 */
export function canPerformAction(role: string | undefined, action: string, resource?: string): boolean {
  if (!role) return false;
  const permissionKey = resource ? `${resource}.${action}` : action;
  return hasRolePermission(role, permissionKey);
}

/**
 * Checks whether a role holds at least one of the given permission keys.
 */
export function hasAnyPermission(role: string | undefined, permissions: Array<PermissionKey | string>): boolean {
  return permissions.some((permission) => hasRolePermission(role, permission));
}

/** Widest first: a role holding both `view_all` and `view_assigned` gets `ALL`. */
const VIEW_SCOPE_SUFFIXES: ReadonlyArray<readonly [ViewScope, string]> = [
  ['ALL', 'view_all'],
  ['ASSIGNED', 'view_assigned'],
  ['SELF', 'view_self'],
];

/**
 * Resolves how much of a resource a role may read.
 * Example: resolveViewScope('TEACHER', 'transactions') => 'ASSIGNED'
 */
export function resolveViewScope(role: string | undefined, resource: string): ViewScope {
  const match = VIEW_SCOPE_SUFFIXES.find(([, suffix]) => hasRolePermission(role, `${resource}.${suffix}`));
  return match ? match[0] : 'NONE';
}

/**
 * Which base routes may enter each top-level route section. Gating on base
 * route rather than role name means a new role inherits the correct shell.
 */
export const ROUTE_SECTION_ACCESS: Record<string, string[]> = {
  '/admin': ['/admin'],
  '/teacher': ['/admin', '/teacher'],
  '/student': ['/student'],
};

/**
 * Checks whether a role may enter a top-level route section.
 */
export function canAccessRouteSection(role: string | undefined, section: string): boolean {
  if (!role) return false;
  const allowedBaseRoutes = ROUTE_SECTION_ACCESS[section];
  if (!allowedBaseRoutes) return false;
  return allowedBaseRoutes.includes(getRoleBaseRoute(role));
}

/**
 * Checks whether a role is defined in the system. Route guards must test this
 * before trusting getRoleBaseRoute, which falls back to '/admin' for unknowns.
 */
export function isKnownRole(role?: string): boolean {
  if (!role) return false;
  return Boolean(ROLE_BASE_ROUTES[role.toUpperCase().trim()]);
}
